import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Button, Row, Col, Form, Alert, Table, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const API_URL = 'https://mixta39.dev/phpapi/api/ausencias.php';

function todayLocalYYYYMMDD() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const turnos = ['Matutino', 'Vespertino'];
const grados = ['Primero', 'Segundo', 'Tercero'];
const grupos = ['A', 'B', 'C'];

const reportesDefault = [
  {
    id: 1,
    titulo: 'Reporte de Faltas del Día Actual',
    descripcion: 'Muestra las faltas de los estudiantes del día actual.',
    imagen: 'https://via.placeholder.com/150',
  },
  {
    id: 2,
    titulo: 'Reporte de Faltas por Rango de Fechas',
    descripcion: 'Muestra las faltas de los estudiantes en un rango de fechas.',
    imagen: 'https://via.placeholder.com/150',
  },
];

function Reportes() {
  const currentDate = todayLocalYYYYMMDD();

  const [turno, setTurno] = useState('');
  const [grado, setGrado] = useState('');
  const [grupo, setGrupo] = useState('');
  const [fechaInicio, setFechaInicio] = useState(currentDate);
  const [fechaFin, setFechaFin] = useState(currentDate);
  const [reportes, setReportes] = useState([]);
  const [fechaError, setFechaError] = useState(false);
  const [mostrarTabla, setMostrarTabla] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Para cancelar requests previos si el usuario vuelve a disparar
  const controllerRef = useRef(null);

  useEffect(() => {
    setFechaError(Boolean(fechaInicio && fechaFin && fechaInicio > fechaFin));
  }, [fechaInicio, fechaFin]);

  // Orden natural para tablas-resumen
  const gradoOrder = useMemo(() => Object.fromEntries(grados.map((g, i) => [g, i])), []);
  const grupoOrder = useMemo(() => Object.fromEntries(grupos.map((g, i) => [g, i])), []);

  async function obtenerReportes({ useTodayOnly = false, triggerId = null } = {}) {
    setErrorMsg('');
    setLoadingId(triggerId);

    // Cancela si hay una solicitud previa en curso
    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = new AbortController();

    try {
      const start = useTodayOnly ? currentDate : fechaInicio;
      const end = useTodayOnly ? currentDate : fechaFin;

      const payload = {
        Turno: turno,
        StartDate: start,
        EndDate: end,
        Grado: grado || null,
        Grupo: grupo || null,
      };

      const { data } = await axios.post(API_URL, payload, {
        headers: { 'Content-Type': 'application/json' },
        signal: controllerRef.current.signal,
      });

      setReportes(Array.isArray(data) ? data : []);
      setMostrarTabla(true);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return; // solicitud cancelada
      }
      console.error('Error al obtener los reportes:', err);
      setErrorMsg('No se pudieron cargar los reportes. Intenta de nuevo.');
      setMostrarTabla(false);
    } finally {
      setLoadingId(null);
    }
  }

  async function handleGenerarReporte({ useTodayOnly = false, triggerId = null } = {}) {
    if (!turno) {
      setErrorMsg('Selecciona un turno para generar el reporte.');
      return;
    }
    if (loadingId !== null) return; // evita doble click

    if (useTodayOnly) {
      await obtenerReportes({ useTodayOnly: true, triggerId });
      return;
    }

    if (fechaInicio && fechaFin && !fechaError) {
      await obtenerReportes({ useTodayOnly: false, triggerId });
    } else {
      setErrorMsg('Por favor, verifica el rango de fechas.');
    }
  }

  // === Agrupa por Grado -> Grupo (para las tablas de detalle) ===
  const groupedReportes = useMemo(() => {
    const grouped = reportes.reduce((acc, cur) => {
      const g = cur.Grado ?? '—';
      const gr = cur.Grupo ?? '—';
      if (!acc[g]) acc[g] = {};
      if (!acc[g][gr]) acc[g][gr] = [];
      acc[g][gr].push(cur);
      return acc;
    }, {});

    const sortKeys = (keys, orderMap) =>
      keys.sort((a, b) => {
        const ia = orderMap[a] ?? Number.MAX_SAFE_INTEGER;
        const ib = orderMap[b] ?? Number.MAX_SAFE_INTEGER;
        return ia - ib || String(a).localeCompare(String(b));
      });

    const sorted = {};
    const gradosKeys = sortKeys(Object.keys(grouped), gradoOrder);
    gradosKeys.forEach((gk) => {
      const gruposObj = grouped[gk];
      const gruposKeys = sortKeys(Object.keys(gruposObj), grupoOrder);
      sorted[gk] = {};
      gruposKeys.forEach((grk) => (sorted[gk][grk] = gruposObj[grk]));
    });
    return sorted;
  }, [reportes, gradoOrder, grupoOrder]);

  // === FILAS para la tabla principal agrupando también por FECHA ===
  const resumenFilas = useMemo(() => {
    const filas = [];
    Object.keys(groupedReportes).forEach((g) => {
      Object.keys(groupedReportes[g]).forEach((gr) => {
        const list = groupedReportes[g][gr];
        // agrupa por Fecha
        const byDate = list.reduce((acc, cur) => {
          const f = cur.Fecha ?? '—';
          if (!acc[f]) acc[f] = 0;
          acc[f] += 1;
          return acc;
        }, {});
        // ordena fechas
        const fechas = Object.keys(byDate).sort((a, b) => {
          if (a === '—') return 1;
          if (b === '—') return -1;
          return a.localeCompare(b);
        });
        fechas.forEach((f) => {
          filas.push({ Grado: g, Grupo: gr, Fecha: f, Conteo: byDate[f] });
        });
      });
    });

    // orden final: Grado (natural), Grupo (natural), Fecha (asc)
    const gradoIdx = (v) => gradoOrder[v] ?? Number.MAX_SAFE_INTEGER;
    const grupoIdx = (v) => grupoOrder[v] ?? Number.MAX_SAFE_INTEGER;
    return filas.sort((a, b) => {
      const g = gradoIdx(a.Grado) - gradoIdx(b.Grado);
      if (g !== 0) return g;
      const gr = grupoIdx(a.Grupo) - grupoIdx(b.Grupo);
      if (gr !== 0) return gr;
      if (a.Fecha === '—') return 1;
      if (b.Fecha === '—') return -1;
      return a.Fecha.localeCompare(b.Fecha);
    });
  }, [groupedReportes, gradoOrder, grupoOrder]);

  const rangoTexto = useMemo(() => {
    const esHoy = fechaInicio === currentDate && fechaFin === currentDate;
    return esHoy ? `Rango: ${currentDate}` : `Rango: ${fechaInicio} a ${fechaFin}`;
  }, [fechaInicio, fechaFin, currentDate]);

  const exportarApdf = (tableId, titulo, { start, end }) => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(titulo, 14, 14);
    doc.setFontSize(10);
    doc.text(`Periodo: ${start === end ? start : `${start} a ${end}`}`, 14, 20);

    const table = document.getElementById(tableId);
    if (!table) return;
    doc.autoTable({ html: table, startY: 26 });

    const nombreArchivo = `reporte_${tableId}_${start}${start === end ? '' : `_a_${end}`}.pdf`;
    doc.save(nombreArchivo);
  };

  return (
    <div>
      <h2>Reportes</h2>
      <small className="text-muted">{rangoTexto}</small>
      <hr style={{ border: '1px solid #ddd', margin: '12px 0 20px' }} />

      {errorMsg && (
        <Alert variant="danger" onClose={() => setErrorMsg('')} dismissible>
          {errorMsg}
        </Alert>
      )}

      {!turno && (
        <Alert variant="warning">
          <strong>Selecciona un turno para comenzar</strong>
        </Alert>
      )}

      <Form>
        <Row className="mb-3">
          <Col xs={12} md={4}>
            <Form.Group controlId="turno">
              <Form.Label>Turno</Form.Label>
              <Form.Select value={turno} onChange={(e) => setTurno(e.target.value)} required>
                <option value="">Seleccione un turno</option>
                {turnos.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col xs={12} md={4}>
            <Form.Group controlId="grado">
              <Form.Label>Grado</Form.Label>
              <Form.Select value={grado} onChange={(e) => setGrado(e.target.value)}>
                <option value="">Todos</option>
                {grados.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col xs={12} md={4}>
            <Form.Group controlId="grupo">
              <Form.Label>Grupo</Form.Label>
              <Form.Select value={grupo} onChange={(e) => setGrupo(e.target.value)}>
                <option value="">Todos</option>
                {grupos.map((gr) => (
                  <option key={gr} value={gr}>
                    {gr}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col xs={12} md={6}>
            <Form.Group controlId="fechaInicio">
              <Form.Label>Fecha de Inicio</Form.Label>
              <Form.Control
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                required
                max={currentDate}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group controlId="fechaFin">
              <Form.Label>Fecha de Fin</Form.Label>
              <Form.Control
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                required
                max={currentDate}
              />
            </Form.Group>
          </Col>
        </Row>

        {fechaError && (
          <Alert variant="danger">La fecha de inicio no puede ser mayor que la fecha de fin.</Alert>
        )}
      </Form>

      {turno && (
        <Row xs={1} sm={2} md={3} lg={4} className="g-4">
          {reportesDefault.map((reporte) => {
            const isDisabled =
              !turno ||
              loadingId !== null ||
              (reporte.id === 2 && (fechaError || !fechaInicio || !fechaFin));
            return (
              <Col key={reporte.id}>
                <Card>
                  <Card.Img variant="top" src={reporte.imagen} alt={reporte.titulo} />
                  <Card.Body>
                    <Card.Title>{reporte.titulo}</Card.Title>
                    <Card.Text>{reporte.descripcion}</Card.Text>
                    <Button
                      variant="primary"
                      onClick={() =>
                        handleGenerarReporte({
                          useTodayOnly: reporte.id === 1,
                          triggerId: reporte.id,
                        })
                      }
                      disabled={isDisabled}
                    >
                      {loadingId === reporte.id ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" /> Generando...
                        </>
                      ) : (
                        'Generar'
                      )}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      <hr style={{ border: '1px solid #ddd', margin: '20px 0' }} />

      {mostrarTabla && reportes.length === 0 && (
        <Alert variant="info">No se encontraron faltas con los filtros seleccionados.</Alert>
      )}

      {/* === TABLA PRINCIPAL: agrupada por Grado, Grupo y Fecha === */}
      {mostrarTabla && resumenFilas.length > 0 && (
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Grado</th>
              <th>Grupo</th>
              <th>Fecha</th>
              <th>Estudiantes</th>
            </tr>
          </thead>
          <tbody>
            {resumenFilas.map((r) => (
              <tr key={`${r.Grado}-${r.Grupo}-${r.Fecha}`}>
                <td>{r.Grado}</td>
                <td>{r.Grupo}</td>
                <td>{r.Fecha}</td>
                <td>{r.Conteo}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* === Tablas por grupo con separadores de fecha === */}
      {mostrarTabla &&
        Object.keys(groupedReportes).map((g) =>
          Object.keys(groupedReportes[g]).map((gr) => {
            const studentsInGroup = groupedReportes[g][gr];

            // Agrupar por Fecha
            const byDate = studentsInGroup.reduce((acc, cur) => {
              const f = cur.Fecha ?? '—';
              if (!acc[f]) acc[f] = [];
              acc[f].push(cur);
              return acc;
            }, {});
            const sortedDates = Object.keys(byDate).sort((a, b) => {
              if (a === '—') return 1;
              if (b === '—') return -1;
              return a.localeCompare(b);
            });

            const start = fechaInicio;
            const end = fechaFin;
            const tableId = `${g}-${gr}`.replace(/\s+/g, '_');

            return (
              <div key={tableId} className="mb-4">
                <div className="d-flex align-items-center justify-content-between">
                  <h3 className="mb-3">{`Grado ${g} - Grupo ${gr}`}</h3>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      exportarApdf(tableId, `Grado ${g} - Grupo ${gr}`, { start, end })
                    }
                  >
                    Exportar a PDF
                  </Button>
                </div>

                <Table id={tableId} striped bordered hover>
                  <thead>
                    <tr>
                      <th>Estudiante ID</th>
                      <th>Apellido Paterno</th>
                      <th>Apellido Materno</th>
                      <th>Nombre</th>
                      <th>Turno</th>
                      <th>Teléfono de Contacto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDates.map((fecha) => (
                      <React.Fragment key={`${tableId}-${fecha}`}>
                        {/* Fila separadora con la FECHA y subtotal */}
                        <tr className="table-secondary">
                          <td colSpan={6} style={{ fontWeight: 600 }}>
                            Fecha: {fecha} — Total: {byDate[fecha].length}
                          </td>
                        </tr>
                        {byDate[fecha].map((student) => (
                          <tr key={`${student.EstudianteID}-${fecha}`}>
                            <td>{student.EstudianteID}</td>
                            <td>{student.ApellidoPaterno}</td>
                            <td>{student.ApellidoMaterno}</td>
                            <td>{student.Nombre}</td>
                            <td>{student.Turno}</td>
                            <td>{student.TelefonoDeContacto || 'No disponible'}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
                <hr />
              </div>
            );
          })
        )}
    </div>
  );
}

export default Reportes;
